import React, { useEffect, useState } from "react";
import api from "../lib/api";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, PieChart, Pie, Legend,
} from "recharts";
import { TrendingUp, Users, Calendar as CalIcon, Loader2 } from "lucide-react";

const NAVY = "#0F2A5F";
const TEAL = "#14B8A6";
const AMBER = "#f59e0b";
const RED = "#e11d48";
const SLATE = "#64748b";
const PALETTE = [TEAL, NAVY, AMBER, "#8b5cf6", "#0ea5e9", RED, "#22c55e"];

const AverageCard = ({ label, total, avg, icon: Icon, color }) => (
  <div className="border border-slate-200 rounded-lg p-3">
    <div className="flex items-center gap-2 mb-2">
      <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: `${color}18`, color }}>
        <Icon size={13} />
      </div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    </div>
    <div className="text-lg font-bold text-slate-900">{total}</div>
    <div className="text-[11px] text-slate-500">avg {avg}/day</div>
  </div>
);

const DashboardAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/dashboard/analytics");
        setData(res.data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-400 text-sm">
      <Loader2 size={18} className="animate-spin mx-auto mb-2" /> Loading analytics...
    </div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={18} className="text-[#14B8A6]" />
          <h3 className="font-semibold text-slate-800">Practice Averages</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase mb-2">New Patients</div>
            <div className="grid grid-cols-3 gap-2">
              <AverageCard label="7 days" total={data.patient_averages.last_7_days.total} avg={data.patient_averages.last_7_days.avg_per_day} icon={Users} color={TEAL} />
              <AverageCard label="30 days" total={data.patient_averages.last_30_days.total} avg={data.patient_averages.last_30_days.avg_per_day} icon={Users} color={NAVY} />
              <AverageCard label="1 year" total={data.patient_averages.last_365_days.total} avg={data.patient_averages.last_365_days.avg_per_day} icon={Users} color={AMBER} />
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Appointments</div>
            <div className="grid grid-cols-3 gap-2">
              <AverageCard label="7 days" total={data.appointment_averages.last_7_days.total} avg={data.appointment_averages.last_7_days.avg_per_day} icon={CalIcon} color={TEAL} />
              <AverageCard label="30 days" total={data.appointment_averages.last_30_days.total} avg={data.appointment_averages.last_30_days.avg_per_day} icon={CalIcon} color={NAVY} />
              <AverageCard label="1 year" total={data.appointment_averages.last_365_days.total} avg={data.appointment_averages.last_365_days.avg_per_day} icon={CalIcon} color={AMBER} />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <ChartCard title="Age Distribution">
          {data.age_distribution.length === 0 ? <EmptyMsg /> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.age_distribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: SLATE }} />
                <YAxis tick={{ fontSize: 11, fill: SLATE }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="count" fill={TEAL} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Sex Distribution">
          {data.gender_distribution.length === 0 ? <EmptyMsg /> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={data.gender_distribution} dataKey="count" nameKey="gender" cx="50%" cy="50%" outerRadius={70} label={({ name, value }) => `${name}: ${value}`}>
                  {data.gender_distribution.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Top Procedures">
          {data.procedure_distribution.length === 0 ? <EmptyMsg /> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.procedure_distribution} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 10, fill: SLATE }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: SLATE }} width={110} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="count" fill={NAVY} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>
    </div>
  );
};

const ChartCard = ({ title, children }) => (
  <div className="bg-white rounded-lg border border-slate-200 p-4">
    <h4 className="font-semibold text-slate-800 text-sm mb-3">{title}</h4>
    {children}
  </div>
);

const EmptyMsg = () => <div className="h-[220px] flex items-center justify-center text-xs text-slate-400">No data yet</div>;

export default DashboardAnalytics;
